import React, { useCallback, useEffect, useState } from "react";
import { Plus, X, Mail, Phone, Trash2, Pencil, Users, Lock, EyeOff, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const VIS_LABELS = {
    all_staff: "Tutto lo staff",
    assigned_only: "Solo assegnatari",
    admin_only: "Solo admin",
};

function VisibilityBadge({ v }) {
    const label = VIS_LABELS[v] || v;
    const bg = v === "admin_only" ? "#3A2E00" : v === "assigned_only" ? "#2E2A1A" : "#1F2A22";
    const fg = v === "admin_only" ? "#E5FF00" : "#EFEFEC";
    const Icon = v === "admin_only" ? Lock : v === "assigned_only" ? EyeOff : Users;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "3px 10px", background: bg, color: fg,
            borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace',
            fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase",
        }}>
            <Icon size={11} strokeWidth={2} /> {label}
        </span>
    );
}

function ContactForm({ initial, staffUsers, onSubmit, onCancel, submitting }) {
    const [form, setForm] = useState({
        name: initial?.name || "",
        role_label: initial?.role_label || "",
        email: initial?.email || "",
        phone: initial?.phone || "",
        notes: initial?.notes || "",
        internal_visibility: initial?.internal_visibility || "all_staff",
        assigned_user_ids: initial?.assigned_user_ids || [],
        visible_to_client: !!initial?.visible_to_client,
    });
    const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
    const toggleAssigned = (uid) => setForm((prev) => ({
        ...prev,
        assigned_user_ids: prev.assigned_user_ids.includes(uid)
            ? prev.assigned_user_ids.filter((x) => x !== uid)
            : [...prev.assigned_user_ids, uid],
    }));
    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="lt-scad-form" data-testid="contact-form">
            <div className="lt-field"><label className="lt-label">Nome *</label>
                <input className="lt-input" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="contact-form-name" />
            </div>
            <div className="lt-field"><label className="lt-label">Ruolo</label>
                <input className="lt-input" value={form.role_label} onChange={(e) => update("role_label", e.target.value)} placeholder="Es. Amministratore, Responsabile HR" data-testid="contact-form-role" />
            </div>
            <div className="lt-field"><label className="lt-label">Email</label>
                <input type="email" className="lt-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="contact-form-email" />
            </div>
            <div className="lt-field"><label className="lt-label">Telefono</label>
                <input className="lt-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="contact-form-phone" />
            </div>
            <div className="lt-field"><label className="lt-label">Note</label>
                <textarea className="lt-textarea" rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} maxLength={2000} data-testid="contact-form-notes" />
            </div>
            <div className="lt-field">
                <label className="lt-label">Visibilità interna *</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="contact-form-visibility">
                    {Object.entries(VIS_LABELS).map(([k, l]) => (
                        <button
                            key={k} type="button"
                            className={"lt-radio-chip" + (form.internal_visibility === k ? " is-active" : "")}
                            onClick={() => update("internal_visibility", k)}
                            data-testid={`contact-form-vis-${k}`}
                        >{l}</button>
                    ))}
                </div>
            </div>
            {form.internal_visibility === "assigned_only" && (
                <div className="lt-field" data-testid="contact-form-assigned">
                    <label className="lt-label">Assegnatari (staff con accesso completo)</label>
                    <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #E3E5E1", borderRadius: 6, padding: 6 }}>
                        {staffUsers.length === 0 ? <div style={{ padding: 8, color: "#8A8F88", fontSize: 12 }}>Caricamento…</div> :
                            staffUsers.map((u) => (
                                <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer" }}>
                                    <input type="checkbox" checked={form.assigned_user_ids.includes(u.id)} onChange={() => toggleAssigned(u.id)} />
                                    <span>{u.name} <span style={{ color: "#8A8F88", fontSize: 12 }}>· {u.email}</span></span>
                                </label>
                            ))
                        }
                    </div>
                </div>
            )}
            <div className="lt-toggle-row">
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" checked={form.visible_to_client} onChange={(e) => update("visible_to_client", e.target.checked)} data-testid="contact-form-visible-client" />
                    <span>Visibile al cliente (Portale)</span>
                </label>
            </div>
            <div className="lt-form-actions">
                <button type="button" className="lt-btn-secondary" onClick={onCancel} disabled={submitting} data-testid="contact-form-cancel">Annulla</button>
                <button type="submit" className="lt-btn-primary" disabled={submitting} data-testid="contact-form-save">{submitting ? "Salvataggio…" : "Salva"}</button>
            </div>
        </form>
    );
}

export default function ClientContactsTab({ clientId }) {
    const { user: me } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [staff, setStaff] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/internal/clients/${clientId}/contacts`);
            setItems(data.items || []);
        } catch { toast.error("Errore caricamento contatti"); }
        setLoading(false);
    }, [clientId]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/internal/internal-users", { params: { active: true } })
            .then(({ data }) => setStaff(data.items || [])).catch(() => {});
    }, []);

    const canEdit = (c) => me?.is_super_admin || me?.role === "admin" || c.created_by === me?.id;
    const isAdmin = me?.is_super_admin || me?.role === "admin";

    const submit = async (form) => {
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (!payload.email) delete payload.email;
            if (payload.internal_visibility !== "assigned_only") payload.assigned_user_ids = [];
            if (editing) {
                await api.patch(`/internal/clients/${clientId}/contacts/${editing.id}`, payload);
                toast.success("Contatto aggiornato");
            } else {
                await api.post(`/internal/clients/${clientId}/contacts`, payload);
                toast.success("Contatto creato");
            }
            setShowForm(false); setEditing(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore salvataggio");
        } finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/internal/clients/${clientId}/contacts/${confirmDelete.id}`);
            toast.success("Contatto eliminato");
            setConfirmDelete(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore eliminazione");
        }
    };

    return (
        <div data-testid="tab-contatti">
            <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
                <h2 className="lt-section-title">Contatti</h2>
                <button className="lt-btn-primary" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="contacts-new-btn">
                    <Plus size={14} /> Nuovo contatto
                </button>
            </div>
            {loading ? <div style={{ padding: 32, color: "#8A8F88" }}>Caricamento…</div> :
             items.length === 0 ? <div style={{ padding: 32, color: "#8A8F88", textAlign: "center" }}>Nessun contatto registrato.</div> :
             <div style={{ display: "grid", gap: 10 }} data-testid="contacts-list">
                 {items.map((c) => (
                     <div key={c.id} className="lt-card" style={{ padding: 16 }} data-testid={`contact-row-${c.id}`}>
                         <div className="flex items-start justify-between" style={{ gap: 12 }}>
                             <div style={{ minWidth: 0, flex: 1 }}>
                                 <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                                     <strong style={{ fontSize: 15 }}>{c.name}</strong>
                                     {c.role_label && <span style={{ color: "#5F6560", fontSize: 13 }}>· {c.role_label}</span>}
                                     <VisibilityBadge v={c.internal_visibility} />
                                     {c.visible_to_client && (
                                         <span style={{
                                             display: "inline-flex", alignItems: "center", gap: 4,
                                             padding: "3px 8px", background: "#E5FF00", color: "#181B20",
                                             borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace',
                                             fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase",
                                         }}><Eye size={11} strokeWidth={2} /> Cliente</span>
                                     )}
                                     {c.masked && (
                                         <span style={{
                                             display: "inline-flex", alignItems: "center", gap: 4,
                                             padding: "3px 8px", background: "#F1E7E7", color: "#8C2C38",
                                             borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace',
                                             fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase",
                                         }}>Dati riservati</span>
                                     )}
                                 </div>
                                 {!c.masked && (
                                     <div style={{ display: "flex", gap: 16, color: "#5F6560", fontSize: 13, flexWrap: "wrap" }}>
                                         {c.email && <a href={`mailto:${c.email}`} className="lt-link"><Mail size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />{c.email}</a>}
                                         {c.phone && <a href={`tel:${c.phone}`} className="lt-link"><Phone size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />{c.phone}</a>}
                                     </div>
                                 )}
                                 {!c.masked && c.notes && <div style={{ marginTop: 8, fontSize: 13, color: "#3A3F3A" }}>{c.notes}</div>}
                                 {c.internal_visibility === "assigned_only" && c.assigned_user_ids?.length > 0 && (
                                     <div style={{ marginTop: 8, fontSize: 12, color: "#8A8F88" }}>
                                         Assegnatari: {c.assigned_user_ids.map((uid) => staff.find((s) => s.id === uid)?.name || uid).join(", ")}
                                     </div>
                                 )}
                             </div>
                             {canEdit(c) && !c.masked && (
                                 <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                                     <button className="lt-icon-btn" onClick={() => { setEditing(c); setShowForm(true); }} aria-label="Modifica" data-testid={`contact-edit-${c.id}`}><Pencil size={14} /></button>
                                     {isAdmin && (
                                         <button className="lt-icon-btn" onClick={() => setConfirmDelete(c)} aria-label="Elimina" data-testid={`contact-delete-${c.id}`}><Trash2 size={14} /></button>
                                     )}
                                 </div>
                             )}
                         </div>
                     </div>
                 ))}
             </div>
            }
            {showForm && (
                <div className="lt-modal-backdrop" onClick={() => !submitting && (setShowForm(false), setEditing(null))} data-testid="contact-form-modal">
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(560px, 92vw)" }}>
                        <div className="lt-modal__head">
                            <h2 className="lt-modal__title">{editing ? "Modifica contatto" : "Nuovo contatto"}</h2>
                            <button className="lt-icon-btn" onClick={() => { setShowForm(false); setEditing(null); }} aria-label="Chiudi"><X size={16} /></button>
                        </div>
                        <ContactForm
                            initial={editing}
                            staffUsers={staff}
                            onSubmit={submit}
                            onCancel={() => { setShowForm(false); setEditing(null); }}
                            submitting={submitting}
                        />
                    </div>
                </div>
            )}
            {confirmDelete && (
                <div className="lt-modal-backdrop" onClick={() => setConfirmDelete(null)} data-testid="contact-delete-modal">
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(420px, 92vw)" }}>
                        <div className="lt-modal__head"><h2 className="lt-modal__title">Eliminare contatto?</h2></div>
                        <div style={{ padding: 20, fontSize: 14, color: "#3A3F3A" }}>Confermi eliminazione di <strong>{confirmDelete.name}</strong>?</div>
                        <div className="lt-form-actions" style={{ padding: "12px 20px" }}>
                            <div style={{ flex: 1 }} />
                            <button className="lt-btn-secondary" onClick={() => setConfirmDelete(null)}>Annulla</button>
                            <button onClick={doDelete} data-testid="contact-delete-confirm" style={{ padding: "9px 16px", background: "#8C2C38", color: "#FFF", border: "none", borderRadius: 3, cursor: "pointer", fontWeight: 600 }}>Elimina</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
